import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, GripVertical, Upload } from "lucide-react";

const empty = { title: "", image_url: "", link: "", display_order: 0, is_active: true };

const AdminStories = () => {
  const [stories, setStories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/admin/stories").then((r) => setStories(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing({ ...empty, display_order: stories.length });
    setOpen(true);
  };
  const openEdit = (s) => { setEditing({ ...s }); setOpen(true); };

  const save = async () => {
    try {
      const body = { ...editing, display_order: Number(editing.display_order || 0) };
      if (editing.id) await api.patch(`/admin/stories/${editing.id}`, body);
      else await api.post(`/admin/stories`, body);
      toast.success("Saved");
      setOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.title}"?`)) return;
    await api.delete(`/admin/stories/${s.id}`);
    toast.success("Deleted");
    load();
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing((ed) => ({ ...ed, image_url: data.url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Cloudinary not configured — paste a URL instead.");
    } finally { setUploading(false); }
  };

  const move = async (s, dir) => {
    const sorted = [...stories].sort((a, b) => a.display_order - b.display_order);
    const idx = sorted.findIndex((x) => x.id === s.id);
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    const other = sorted[swapIdx];
    await api.patch(`/admin/stories/${s.id}`, { ...s, display_order: other.display_order });
    await api.patch(`/admin/stories/${other.id}`, { ...other, display_order: s.display_order });
    load();
  };

  const sorted = [...stories].sort((a, b) => a.display_order - b.display_order);

  return (
    <div data-testid="admin-stories-page">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <p className="eyebrow mb-1">Homepage</p>
          <h1 className="font-hero text-3xl">Stories rail</h1>
          <p className="text-sm text-charcoal/70 mt-1 max-w-lg">The Instagram-style circular rail that appears under the hero. Drag or use the arrows to reorder.</p>
        </div>
        <button className="btn-primary" onClick={openNew} data-testid="admin-new-story-button">
          <Plus size={14} className="mr-2" /> New story
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-border overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="text-left font-label text-[11px] uppercase tracking-[0.22em] text-charcoal/70 border-b border-border">
              <th className="py-3 px-4 w-16">Order</th><th>Story</th><th>Link</th><th>Status</th><th className="text-right px-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((s, i) => (
              <tr key={s.id} className="border-b border-border last:border-b-0" data-testid={`admin-story-row-${s.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <td className="py-3 px-4">
                  <div className="flex items-center gap-1">
                    <GripVertical size={14} className="text-charcoal/70" />
                    <div className="flex flex-col">
                      <button className="text-charcoal/70 hover:text-charcoal leading-none" onClick={() => move(s, -1)} disabled={i === 0} aria-label="up">▲</button>
                      <button className="text-charcoal/70 hover:text-charcoal leading-none" onClick={() => move(s, 1)} disabled={i === sorted.length - 1} aria-label="down">▼</button>
                    </div>
                  </div>
                </td>
                <td>
                  <div className="flex items-center gap-3">
                    <div
                      className="w-[54px] h-[54px] rounded-full p-[2px]"
                      style={{ background: "linear-gradient(135deg, #F3D6E2, #E6E2F5)" }}
                    >
                      <div className="w-full h-full rounded-full bg-cream p-[2px]">
                        <div className="w-full h-full rounded-full overflow-hidden">
                          {s.image_url && <img src={s.image_url} alt="" className="w-full h-full object-cover" />}
                        </div>
                      </div>
                    </div>
                    <span className="font-cormorant text-lg">{s.title}</span>
                  </div>
                </td>
                <td className="text-charcoal/70">{s.link || "—"}</td>
                <td>{s.is_active ? <span className="text-emerald-600 text-xs uppercase tracking-[0.2em]">Live</span> : <span className="text-charcoal/70 text-xs uppercase tracking-[0.2em]">Hidden</span>}</td>
                <td className="text-right px-4">
                  <button className="p-2 text-charcoal/70 hover:text-charcoal" onClick={() => openEdit(s)} aria-label="edit"><Pencil size={14} /></button>
                  <button className="p-2 text-charcoal/70 hover:text-rose" onClick={() => remove(s)} aria-label="delete"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={(v) => (v ? null : setOpen(false))}>
        <DialogContent className="bg-cream max-w-lg">
          <DialogHeader><DialogTitle className="font-hero text-2xl">{editing?.id ? "Edit" : "New"} story</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <label className="eyebrow">Title</label>
                <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="story-title-input" />
              </div>
              <div>
                <label className="eyebrow">Link (optional)</label>
                <Input value={editing.link || ""} onChange={(e) => setEditing({ ...editing, link: e.target.value })} placeholder="/shop/necklaces or https://…" data-testid="story-link-input" />
              </div>
              <div>
                <label className="eyebrow block mb-2">Image</label>
                {editing.image_url && (
                  <div className="mb-3 flex items-center gap-3">
                    <div className="w-[54px] h-[54px] rounded-full p-[2px]" style={{ background: "linear-gradient(135deg, #F3D6E2, #E6E2F5)" }}>
                      <div className="w-full h-full rounded-full bg-cream p-[2px]">
                        <div className="w-full h-full rounded-full overflow-hidden">
                          <img src={editing.image_url} alt="" className="w-full h-full object-cover" />
                        </div>
                      </div>
                    </div>
                    <span className="text-xs text-charcoal/70 truncate max-w-[220px]">{editing.image_url}</span>
                  </div>
                )}
                <div className="flex gap-2">
                  <label className="btn-outline cursor-pointer">
                    <Upload size={14} className="mr-2" /> {uploading ? "Uploading…" : "Upload (Cloudinary)"}
                    <input type="file" className="hidden" accept="image/*" onChange={onUpload} disabled={uploading} />
                  </label>
                  <Input
                    placeholder="Or paste an image URL"
                    value={editing.image_url || ""}
                    onChange={(e) => setEditing({ ...editing, image_url: e.target.value })}
                    data-testid="story-image-url-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="eyebrow">Display order</label>
                  <Input type="number" value={editing.display_order || 0} onChange={(e) => setEditing({ ...editing, display_order: Number(e.target.value) })} />
                </div>
                <label className="flex items-center gap-2 font-label text-[12px] uppercase tracking-[0.2em] text-charcoal self-end pb-2">
                  <input type="checkbox" checked={editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} className="accent-rose" />
                  Active
                </label>
              </div>
              <div className="flex justify-end gap-3 pt-3">
                <button className="btn-outline" onClick={() => setOpen(false)}>Cancel</button>
                <button className="btn-primary" onClick={save} data-testid="admin-save-story">Save story</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminStories;
